import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";

import type { ShellState, ShellStoreApi, WorkShapes } from "./session.js";

/**
 * `P0-UIK-013`: the shell store's React entry point.
 *
 * The store itself is vanilla Zustand (`session.ts`) so the rule of 4.6.5 can
 * be tested without a renderer. This adds only what React needs: one context,
 * and a selector hook built on `useSyncExternalStore` so a screen re-renders
 * for the slice it reads and not for every keystroke in another screen's form.
 *
 * **Danger sentence.** The failure a missing provider produces is a hook
 * returning `undefined` deep inside a screen, surfacing as *"cannot read
 * property of undefined"* three components away from its cause. So the hook
 * throws with the actual cause instead.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- one context serves every app's persona union and work shapes; the typed hooks below restore the app's own types at the point of use.
const ShellStoreContext = createContext<ShellStoreApi<any, any> | null>(null);

export interface ShellStoreProviderProps<Persona extends string, W extends WorkShapes> {
  readonly store: ShellStoreApi<Persona, W>;
  readonly children: ReactNode;
}

export function ShellStoreProvider<Persona extends string, W extends WorkShapes>({
  store,
  children,
}: ShellStoreProviderProps<Persona, W>): React.JSX.Element {
  return <ShellStoreContext.Provider value={store}>{children}</ShellStoreContext.Provider>;
}

export function useShellStoreApi<Persona extends string, W extends WorkShapes>(): ShellStoreApi<Persona, W> {
  const store = useContext(ShellStoreContext);
  if (store === null) {
    throw new Error(
      "useShellStore was called outside <ShellStoreProvider>; the shell store has no value to give",
    );
  }
  return store as ShellStoreApi<Persona, W>;
}

/** Reads one slice. The selector is what keeps an unrelated form's keystroke from re-rendering this screen. */
export function useShellStore<Persona extends string, W extends WorkShapes, Slice>(
  select: (state: ShellState<Persona, W>) => Slice,
): Slice {
  const store = useShellStoreApi<Persona, W>();
  return useSyncExternalStore(
    store.subscribe,
    () => select(store.getState()),
    () => select(store.getState()),
  );
}
